import { create } from 'zustand';
import { useEffect } from 'react';
import { Recipe } from '@/types/recipe';

export interface ImageGenerationState {
  isOpen: boolean;
  recipe: Recipe | null;
  source: 'detail-page' | 'assistant' | 'form' | 'studio' | null;
  isGenerating: boolean;
  progress: number;
  currentStep: string;
  error: string | null;
}

export interface ImageGenerationActions {
  openModal: (recipe: Recipe, source: 'detail-page' | 'assistant' | 'form' | 'studio') => void;
  closeModal: () => void;
  setGenerating: (isGenerating: boolean) => void;
  setProgress: (progress: number, step: string) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

interface ImageGenerationModalStore extends ImageGenerationState, ImageGenerationActions {}

const initialState: ImageGenerationState = {
  isOpen: false,
  recipe: null,
  source: null,
  isGenerating: false,
  progress: 0,
  currentStep: '',
  error: null,
};

export const useImageGenerationModalStore = create<ImageGenerationModalStore>((set, _get) => ({
  ...initialState,

  openModal: (recipe: Recipe, source: 'detail-page' | 'assistant' | 'form' | 'studio') => {
    set({
      isOpen: true,
      recipe,
      source,
      isGenerating: false,
      progress: 0,
      currentStep: '',
      error: null,
    });
  },

  closeModal: () => {
    set({
      isOpen: false,
      recipe: null,
      source: null,
      isGenerating: false,
      progress: 0,
      currentStep: '',
      error: null,
    });
  },

  setGenerating: (isGenerating: boolean) => {
    set({ isGenerating });
  },

  setProgress: (progress: number, step: string) => {
    set({ progress, currentStep: step });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  reset: () => {
    set(initialState);
  },
}));

// Global modal service for programmatic access
export class ImageGenerationModalService {
  static openModal(recipe: Recipe, source: 'detail-page' | 'assistant' | 'form' | 'studio') {
    useImageGenerationModalStore.getState().openModal(recipe, source);
  }

  static closeModal() {
    useImageGenerationModalStore.getState().closeModal();
  }

  static getState() {
    return useImageGenerationModalStore.getState();
  }
}

// Event system for cross-component communication
export interface ImageGenerationEvents {
  'image-generation-start': { recipeId: string; source: string };
  'image-generation-progress': { recipeId: string; progress: number; step: string };
  'image-generation-complete': { recipeId: string; imageData: string; prompt: string };
  'image-generation-error': { recipeId: string; error: string };
  'image-applied-to-recipe': { recipeId: string; imageData: string };
  'image-generation-cancelled': { recipeId: string };
}

export class ImageGenerationEventBus {
  private static listeners: Map<keyof ImageGenerationEvents, Array<(data: any) => void>> = new Map();

  static on<K extends keyof ImageGenerationEvents>(
    event: K,
    callback: (data: ImageGenerationEvents[K]) => void
  ) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  static off<K extends keyof ImageGenerationEvents>(
    event: K,
    callback: (data: ImageGenerationEvents[K]) => void
  ) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  static emit<K extends keyof ImageGenerationEvents>(
    event: K,
    data: ImageGenerationEvents[K]
  ) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => callback(data));
    }
  }

  static clear() {
    this.listeners.clear();
  }
}

// Hook for consuming modal events

export function useImageGenerationEvents<K extends keyof ImageGenerationEvents>(
  event: K,
  callback: (data: ImageGenerationEvents[K]) => void
) {
  useEffect(() => {
    ImageGenerationEventBus.on(event, callback);
    return () => {
      ImageGenerationEventBus.off(event, callback);
    };
  }, [event, callback]);
}